import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useStore } from '../../../store/store';
import { API_ENDPOINTS } from '../../../api/endpoints';
import { cancelRequest } from '../../../api/api';
import UsageProgressBar from '../../../components/common/UsageProgressBar';
import ContractPeriods from './ContractPeriods';

const DOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

const SubscriptionCompanyForm = ({ companyId, onClose, onSave }) => {
  const store = useStore();
  const isEdit = !!companyId;

  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [domainError, setDomainError] = useState(null);

  // Form fields
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [downloadLimit, setDownloadLimit] = useState('');
  const [tokenLimit, setTokenLimit] = useState('');
  const [enrichmentCreditLimit, setEnrichmentCreditLimit] = useState('');
  const [maxConcurrentUsers, setMaxConcurrentUsers] = useState('1');
  const [subscriptionExpiry, setSubscriptionExpiry] = useState('');
  const [contractStart, setContractStart] = useState('');

  // Renewal settings (TD-2811). These describe what happens when a period ENDS,
  // so they belong to the contract rather than to any one period of it.
  // Defaults ON for a new company, matching the column default and the rule the
  // migration states: renewal is the commercial default and unticking it is the
  // deliberate act. Edit mode starts false only so the loaded value wins without
  // a flash of the wrong state.
  const [autoRenew, setAutoRenew] = useState(!isEdit);
  const [autoRenewMonths, setAutoRenewMonths] = useState('12');
  const [cancellationNoticeMonths, setCancellationNoticeMonths] = useState('3');
  const [carryOverEnabled, setCarryOverEnabled] = useState(false);

  // Once a company has a contract period, the periods set its limits and expiry
  // and the backend refuses them on this form, so those fields lock here. Null
  // while the period list is still loading: the fields stay locked until it is
  // known, so nothing can be typed into a field the save would then refuse.
  const [hasPeriods, setHasPeriods] = useState(null);

  // Domain management
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [isAddingDomain, setIsAddingDomain] = useState(false);

  // Usage stats (edit mode only)
  const [downloadCount, setDownloadCount] = useState(0);
  const [tokenUsage, setTokenUsage] = useState(0);
  const [enrichmentUsage, setEnrichmentUsage] = useState(0);

  // A period write mirrors onto the company row, so the fields above are stale
  // the moment one is saved. Applied from the row the write returned rather than
  // re-fetched: the panel below already paid for that round trip.
  const applyCompany = useCallback((data) => {
    if (!data) return;
    setDownloadLimit(
      data.profiler_download_limit == null ? '' : String(data.profiler_download_limit)
    );
    setTokenLimit(data.agent_token_limit == null ? '' : String(data.agent_token_limit));
    setEnrichmentCreditLimit(
      data.enrichment_credit_limit == null ? '' : String(data.enrichment_credit_limit)
    );
    setMaxConcurrentUsers(String(data.max_concurrent_users ?? 1));
    setSubscriptionExpiry(data.subscription_expiry ? data.subscription_expiry.split('T')[0] : '');
    // Ending the last period early switches renewal off on the server. The
    // checkbox has to follow, or the next save of this form would switch the
    // renewal of a customer who is leaving back on.
    if (typeof data.auto_renew === 'boolean') setAutoRenew(data.auto_renew);
  }, []);

  // A list that could not be loaded arrives as null and unlocks the fields: the
  // backend still refuses a changed limit for a company that has periods.
  const handlePeriodsLoaded = useCallback(
    (list) => setHasPeriods(Array.isArray(list) && list.length > 0),
    []
  );
  const limitsLocked = isEdit && hasPeriods !== false;

  const loadCompany = useCallback(() => {
    if (!companyId) return;

    setIsLoading(true);
    setDownloadCount(0);
    setTokenUsage(0);
    setEnrichmentUsage(0);

    store.makeRequest({
      endpoint: {
        ...API_ENDPOINTS.SUBSCRIPTION_GET_COMPANY,
        params: { id: companyId }
      },
      onSuccess: (data) => {
        setName(data.name || '');
        setNotes(data.notes || '');
        setDownloadLimit(
          data.profiler_download_limit == null ? '' : String(data.profiler_download_limit)
        );
        setTokenLimit(data.agent_token_limit == null ? '' : String(data.agent_token_limit));
        setEnrichmentCreditLimit(
          data.enrichment_credit_limit == null ? '' : String(data.enrichment_credit_limit)
        );
        setMaxConcurrentUsers(String(data.max_concurrent_users ?? 1));
        setSubscriptionExpiry(
          data.subscription_expiry ? data.subscription_expiry.split('T')[0] : ''
        );
        setContractStart(data.contract_start ? data.contract_start.split('T')[0] : '');
        setAutoRenew(data.auto_renew === true);
        setAutoRenewMonths(String(data.auto_renew_months ?? 12));
        setCancellationNoticeMonths(String(data.cancellation_notice_months ?? 3));
        setCarryOverEnabled(data.carry_over_enabled === true);
        setDomains(data.domains || []);
        setDownloadCount(data.download_count ?? 0);
        setTokenUsage(data.token_usage ?? 0);
        setEnrichmentUsage(data.enrichment_usage ?? 0);
      },
      onError: (msg) => setError(msg || 'Failed to load company'),
      onFinally: () => setIsLoading(false)
    });
  }, [companyId, store]);

  useEffect(() => {
    loadCompany();
    return () => {
      cancelRequest(API_ENDPOINTS.SUBSCRIPTION_GET_COMPANY);
    };
  }, [loadCompany]);

  // The fields the contract periods set once the company has one. Not checked
  // while they are locked: the values on screen came from the backend and are
  // not sent back.
  const validateMirroredFields = () => {
    if (
      downloadLimit !== '' &&
      (Number.isNaN(Number(downloadLimit)) || Number(downloadLimit) < 0)
    ) {
      return 'Download limit must be a non-negative number or empty';
    }
    if (tokenLimit !== '' && (Number.isNaN(Number(tokenLimit)) || Number(tokenLimit) < 0)) {
      return 'Token limit must be a non-negative number or empty';
    }
    if (
      enrichmentCreditLimit !== '' &&
      (Number.isNaN(Number(enrichmentCreditLimit)) ||
        Number(enrichmentCreditLimit) < 0 ||
        !Number.isInteger(Number(enrichmentCreditLimit)))
    ) {
      return 'Contact credit limit must be a non-negative integer or empty';
    }
    const concurrentUsers = Number(maxConcurrentUsers);
    if (
      Number.isNaN(concurrentUsers) ||
      concurrentUsers < 1 ||
      !Number.isInteger(concurrentUsers)
    ) {
      return 'Max concurrent users must be a positive whole number';
    }
    if (contractStart && subscriptionExpiry && subscriptionExpiry < contractStart) {
      return 'Subscription expiry must be after contract start date';
    }
    return null;
  };

  const validateForm = () => {
    if (!name.trim()) {
      return 'Company name is required';
    }
    const mirroredError = limitsLocked ? null : validateMirroredFields();
    if (mirroredError) return mirroredError;

    const renewMonths = Number(autoRenewMonths);
    if (autoRenew && (!Number.isInteger(renewMonths) || renewMonths < 1)) {
      return 'Renewal length must be a positive whole number of months';
    }
    const noticeMonths = Number(cancellationNoticeMonths);
    if (autoRenew && (!Number.isInteger(noticeMonths) || noticeMonths < 0)) {
      return 'Cancellation notice must be a whole number of months, 0 or more';
    }
    return null;
  };

  const handleSave = () => {
    setValidationError(null);
    setError(null);

    const formError = validateForm();
    if (formError) {
      setValidationError(formError);
      return;
    }

    setIsSaving(true);

    // Locked fields stay out of the request altogether: the backend refuses a
    // changed limit or expiry once the company has a period, and an echo of the
    // values on screen would only be a round trip to a no-op.
    const mirrored = limitsLocked
      ? {}
      : {
          profiler_download_limit: downloadLimit === '' ? null : Number(downloadLimit),
          agent_token_limit: tokenLimit === '' ? null : Number(tokenLimit),
          enrichment_credit_limit:
            enrichmentCreditLimit === '' ? null : Number(enrichmentCreditLimit),
          max_concurrent_users: Number(maxConcurrentUsers) || 1,
          subscription_expiry: subscriptionExpiry || null
        };

    const body = {
      name: name.trim(),
      notes: notes.trim() || null,
      ...mirrored,
      contract_start: contractStart || null,
      auto_renew: autoRenew,
      auto_renew_months: Number(autoRenewMonths) || 12,
      cancellation_notice_months: Number(cancellationNoticeMonths),
      carry_over_enabled: carryOverEnabled
    };

    if (isEdit) {
      body.id = companyId;
    }

    store.makeRequest({
      endpoint: isEdit
        ? API_ENDPOINTS.SUBSCRIPTION_UPDATE_COMPANY
        : API_ENDPOINTS.SUBSCRIPTION_CREATE_COMPANY,
      body,
      onSuccess: (data) => {
        onSave?.(data);
      },
      onError: (msg) => setError(msg || 'Failed to save company'),
      onFinally: () => setIsSaving(false)
    });
  };

  const handleAddDomain = () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;

    // Domain management requires a saved company
    if (!companyId) return;

    if (!DOMAIN_REGEX.test(domain)) {
      setDomainError('Please enter a valid domain (e.g. example.com)');
      return;
    }

    setDomainError(null);
    setIsAddingDomain(true);

    store.makeRequest({
      endpoint: API_ENDPOINTS.SUBSCRIPTION_ADD_DOMAIN,
      body: { company_id: companyId, domain },
      onSuccess: (data) => {
        setDomains((prev) => [...prev, data]);
        setNewDomain('');
      },
      onError: (msg) => setDomainError(msg || 'Failed to add domain'),
      onFinally: () => setIsAddingDomain(false)
    });
  };

  const handleRemoveDomain = (domainId, domainName) => {
    if (!globalThis.confirm(`Remove domain "${domainName}"? This cannot be undone.`)) return;

    store.makeRequest({
      endpoint: API_ENDPOINTS.SUBSCRIPTION_REMOVE_DOMAIN,
      body: { domain_id: domainId },
      onSuccess: () => {
        setDomains((prev) => prev.filter((d) => d.id !== domainId));
      },
      onError: (msg) => setDomainError(msg || 'Failed to remove domain')
    });
  };

  if (isLoading) {
    return (
      <div className="subscription-company-form">
        <h3>Edit Company</h3>
        <div className="subscription-form-loading">Loading company details...</div>
      </div>
    );
  }

  return (
    <div className="subscription-company-form">
      <h3>{isEdit ? 'Edit Company' : 'Add Company'}</h3>

      {error && (
        <div className="subscription-form-error" role="alert">
          {error}
        </div>
      )}
      {validationError && (
        <div className="subscription-form-validation-error" role="alert">
          {validationError}
        </div>
      )}

      <div className="subscription-form-group">
        <label htmlFor="company-name">
          Company Name{' '}
          <span className="required-indicator" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id="company-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Company name"
          required
        />
      </div>

      {isEdit && hasPeriods === null && (
        <div className="subscription-form-note">Loading this company&apos;s contract periods.</div>
      )}
      {isEdit && hasPeriods === true && (
        <div className="subscription-form-note">
          Limits and the expiry date are set by this company&apos;s contract periods below: the
          active period&apos;s limits plus anything it carried over, and the last period&apos;s end
          date. Edit the active period to change them.
        </div>
      )}
      {isEdit && !limitsLocked && (
        <div className="subscription-form-note">
          This company has no contract periods yet, so limits and the expiry date are edited here.
          Once a period is added below, the periods set them.
        </div>
      )}

      <div className="subscription-form-group">
        <label htmlFor="download-limit">Download Limit</label>
        <input
          id="download-limit"
          type="number"
          disabled={limitsLocked}
          value={downloadLimit}
          onChange={(e) => setDownloadLimit(e.target.value)}
          placeholder="Leave empty for unlimited"
          min="0"
          step="1"
        />
      </div>

      <div className="subscription-form-group">
        <label htmlFor="token-limit">Token Limit (input + output tokens)</label>
        <input
          id="token-limit"
          type="number"
          disabled={limitsLocked}
          value={tokenLimit}
          onChange={(e) => setTokenLimit(e.target.value)}
          placeholder="Leave empty for unlimited"
          min="0"
          step="1"
        />
      </div>

      <div className="subscription-form-group">
        <label htmlFor="enrichment-credit-limit">
          Contact Credit Limit{' '}
          <span className="subscription-form-hint">
            – requires the enrichment user group AND a credit limit to activate
          </span>
        </label>
        <input
          id="enrichment-credit-limit"
          type="number"
          disabled={limitsLocked}
          value={enrichmentCreditLimit}
          onChange={(e) => setEnrichmentCreditLimit(e.target.value)}
          placeholder="Credit limit (blank = no enrichment)"
          min="0"
          step="1"
        />
      </div>

      <div className="subscription-form-group">
        <label htmlFor="max-concurrent-users">Max Concurrent Users</label>
        <input
          id="max-concurrent-users"
          type="number"
          disabled={limitsLocked}
          value={maxConcurrentUsers}
          onChange={(e) => setMaxConcurrentUsers(e.target.value)}
          min="1"
          step="1"
        />
      </div>

      <div className="subscription-form-group">
        <label htmlFor="contract-start">Contract Start</label>
        <input
          id="contract-start"
          type="date"
          value={contractStart}
          onChange={(e) => setContractStart(e.target.value)}
        />
      </div>

      <div className="subscription-form-group">
        <label htmlFor="subscription-expiry">Subscription Expiry</label>
        <input
          id="subscription-expiry"
          type="date"
          disabled={limitsLocked}
          value={subscriptionExpiry}
          onChange={(e) => setSubscriptionExpiry(e.target.value)}
        />
      </div>

      <div className="subscription-form-group">
        <label htmlFor="notes">Notes</label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes"
          rows={3}
        />
      </div>

      <div className="subscription-form-section">
        <h4>Renewal</h4>
        <div className="subscription-form-group subscription-form-checkbox">
          <input
            id="auto-renew"
            type="checkbox"
            checked={autoRenew}
            onChange={(e) => setAutoRenew(e.target.checked)}
          />
          <label htmlFor="auto-renew">
            Renew automatically{' '}
            <span className="subscription-form-hint">
              – the next period is created overnight once the cancellation notice has passed, with
              the same limits. A contract that has already ended does not renew itself.
            </span>
          </label>
        </div>

        <div className="subscription-form-group">
          <label htmlFor="auto-renew-months">Renewal length (months)</label>
          <input
            id="auto-renew-months"
            type="number"
            min="1"
            step="1"
            value={autoRenewMonths}
            onChange={(e) => setAutoRenewMonths(e.target.value)}
            disabled={!autoRenew}
          />
        </div>

        <div className="subscription-form-group">
          <label htmlFor="cancellation-notice-months">
            Cancellation notice (months){' '}
            <span className="subscription-form-hint">
              – how long before the end the customer can last cancel; the renewal is created the day
              after that deadline
            </span>
          </label>
          <input
            id="cancellation-notice-months"
            type="number"
            min="0"
            step="1"
            value={cancellationNoticeMonths}
            onChange={(e) => setCancellationNoticeMonths(e.target.value)}
            disabled={!autoRenew}
          />
        </div>

        <div className="subscription-form-group subscription-form-checkbox">
          <input
            id="carry-over-enabled"
            type="checkbox"
            checked={carryOverEnabled}
            onChange={(e) => setCarryOverEnabled(e.target.checked)}
          />
          <label htmlFor="carry-over-enabled">
            Carry unused allowance into the next period{' '}
            <span className="subscription-form-hint">– off unless the contract says otherwise</span>
          </label>
        </div>
      </div>

      {isEdit && (
        <>
          <ContractPeriods
            companyId={companyId}
            onCompanyChanged={applyCompany}
            onPeriodsLoaded={handlePeriodsLoaded}
          />

          <div className="subscription-form-section">
            <h4>Usage</h4>
            <UsageProgressBar
              label="Downloads"
              used={downloadCount}
              limit={Number(downloadLimit) || 0}
            />
            <UsageProgressBar
              label="Tokens"
              used={tokenUsage}
              limit={Number(tokenLimit) || 0}
              unit="tokens"
            />
            <UsageProgressBar
              label="Contact credits"
              used={enrichmentUsage}
              limit={Number(enrichmentCreditLimit) || 0}
              unit="credits"
            />
          </div>

          <div className="subscription-form-section">
            <h4>Domains</h4>
            <div className="subscription-domain-list">
              {domains.map((d) => (
                <div key={d.id} className="subscription-domain-item">
                  <span>{d.domain}</span>
                  <button
                    className="subscription-btn-remove-domain"
                    onClick={() => handleRemoveDomain(d.id, d.domain)}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            {domainError && (
              <div className="subscription-domain-error" role="alert">
                {domainError}
              </div>
            )}
            <div className="subscription-domain-add">
              <label htmlFor="add-domain" className="sr-only">
                Add domain
              </label>
              <input
                id="add-domain"
                type="text"
                value={newDomain}
                onChange={(e) => {
                  setNewDomain(e.target.value);
                  setDomainError(null);
                }}
                placeholder="Add domain..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddDomain();
                  }
                }}
              />
              <button
                className="subscription-btn-add-domain"
                onClick={handleAddDomain}
                disabled={isAddingDomain}
                type="button"
              >
                {isAddingDomain ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </>
      )}

      {!isEdit && (
        <div className="subscription-form-note">
          Domains can be added after creating the company.
        </div>
      )}

      <div className="subscription-form-actions">
        <button className="subscription-btn-cancel" onClick={onClose} type="button">
          Cancel
        </button>
        <button
          className="subscription-btn-save"
          onClick={handleSave}
          disabled={isSaving}
          type="button"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

SubscriptionCompanyForm.propTypes = {
  companyId: PropTypes.number,
  onClose: PropTypes.func,
  onSave: PropTypes.func
};

export default SubscriptionCompanyForm;
